import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { GridPager } from "../../common/pagination/GridPager";
import PayrollDataGrid, { payrollActionsCellClass, payrollActionsHeaderClass } from "./PayrollDataGrid";
import PayrollModal from "./PayrollModal";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import { getSelectedCompany } from "../../Global/CustomFunctions";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";

const PAGE_SIZE = 8;
const { SalaryComponent, PayrollStructure } = config.REST_API;
const emptyComponentRow = () => ({ id: `${Date.now()}-${Math.random()}`, componentId: "", unit: "percent", value: "" });
const emptyBuilder = () => ({ name: "", description: "", officeId: "", cycle: "monthly", offCycle: false, rows: [emptyComponentRow()] });
const CYCLE_TO_ENUM = { monthly: 0, biweekly: 1, weekly: 2 };
const STATUS_TO_ENUM = { draft: 0, published: 1, archived: 2 };
const UNIT_TO_ENUM = { percent: 0, fixed: 1 };
const VALUE_FIXED_INPUT_PATTERN = /^\d*(\.\d{0,2})?$/;
const VALUE_FIXED_FINAL_PATTERN = /^\d+(\.\d{1,2})?$/;
const VALUE_PERCENT_INPUT_PATTERN = /^\d{0,3}$/;
const VALUE_PERCENT_FINAL_PATTERN = /^\d+$/;

const normalizeOffice = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  return { id: String(get("id", "Id") ?? ""), name: String(get("name", "Name") ?? ""), countryName: String(get("countryName", "CountryName") ?? "") };
};
const normalizeSalaryComponent = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  return { id: String(get("id", "Id") ?? ""), name: String(get("name", "Name") ?? ""), officeId: String(get("officeId", "OfficeId") ?? ""), isActive: get("isActive", "IsActive") !== false };
};
const unwrapArray = (res) => {
  const body = res?.data;
  if (body == null || body.success === false) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
};
const unwrapOne = (res) => {
  const body = res?.data;
  if (body == null || body.success === false) return null;
  if (body.data && typeof body.data === "object") return body.data;
  if (typeof body === "object") return body;
  return null;
};
const resolveStructureId = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const keys = ["id", "Id", "payrollStructureId", "PayrollStructureId"];
  for (const k of keys) {
    const v = r[k];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return "";
};
const normalizeStructure = (raw, officeMap) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const refsRaw = get("attachedWorkplaceOffices", "AttachedWorkplaceOffices");
  const attachedWorkplaceOffices = Array.isArray(refsRaw) ? refsRaw : [];

  const officeIdsRaw = get("officeIds", "OfficeIds");
  let officeIds = Array.isArray(officeIdsRaw) ? officeIdsRaw.map((x) => String(x)).filter((id) => id && id !== "null" && id !== "undefined") : [];
  if (officeIds.length === 0 && attachedWorkplaceOffices.length > 0) {
    officeIds = attachedWorkplaceOffices
      .map((o) => {
        const v = o && typeof o === "object" ? o.id ?? o.Id : undefined;
        if (v === undefined || v === null) return "";
        const s = String(v).trim();
        return s && s !== "null" && s !== "undefined" ? s : "";
      })
      .filter(Boolean);
  }

  const namesFromRefs = attachedWorkplaceOffices
    .map((o) => (o && typeof o === "object" ? String(o.name ?? o.Name ?? "").trim() : ""))
    .filter(Boolean);
  const namesFromMap = officeIds.map((id) => officeMap.get(String(id))).filter(Boolean);
  const officesLabel = namesFromRefs.length > 0 ? namesFromRefs.join(", ") : namesFromMap.join(", ") || "—";

  const statusVal = Number(get("status", "Status") ?? 0);
  const cycleVal = Number(get("paymentCycle", "PaymentCycle") ?? 0);
  return {
    id: resolveStructureId(r),
    name: String(get("name", "Name") ?? ""),
    status: statusVal === 2 ? "archived" : statusVal === 1 ? "published" : "draft",
    cycle: cycleVal === 2 ? "weekly" : cycleVal === 1 ? "biweekly" : "monthly",
    offCycle: Boolean(get("allowOffCycleRuns", "AllowOffCycleRuns")),
    officeId: officeIds[0] ?? "",
    officeIds,
    offices: officesLabel,
    attachedWorkplaceOffices,
    description: String(get("description", "Description") ?? ""),
    lines: Array.isArray(get("lines", "Lines")) ? get("lines", "Lines") : [],
  };
};
const sanitizeValueInput = (raw, unit) => {
  if (unit === "percent") {
    const digits = String(raw ?? "").replace(/[^\d]/g, "");
    return VALUE_PERCENT_INPUT_PATTERN.test(digits) ? digits : null;
  }
  const next = String(raw ?? "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (next === "") return "";
  const firstDot = next.indexOf(".");
  const normalized = firstDot < 0 ? next : `${next.slice(0, firstDot + 1)}${next.slice(firstDot + 1).replace(/\./g, "")}`;
  return VALUE_FIXED_INPUT_PATTERN.test(normalized) ? normalized : null;
};
const validateLineValue = (row, t) => {
  const raw = String(row?.value ?? "").trim();
  if (!raw) return t("app.payroll.structures.valueRequired");
  if (row?.unit === "percent" && !VALUE_PERCENT_FINAL_PATTERN.test(raw)) return t("app.payroll.structures.valuePercentIntegerError");
  if (row?.unit === "fixed" && !VALUE_FIXED_FINAL_PATTERN.test(raw)) return t("app.payroll.structures.valueFormatError");
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return t("app.payroll.structures.valueFormatError");
  if (row?.unit === "percent" && (amount <= 0 || amount > 100)) return t("app.payroll.structures.valuePercentRangeError");
  if (row?.unit === "fixed" && amount <= 0) return t("app.payroll.structures.valueFixedRangeError");
  return "";
};

const PayrollStructuresPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("payrollStructures");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [builder, setBuilder] = useState(emptyBuilder);
  const [builderMode, setBuilderMode] = useState(/** @type {"create" | "edit"} */ ("create"));
  const [saving, setSaving] = useState(false);
  const [publishingId, setPublishingId] = useState("");
  const [archivingId, setArchivingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewStructure, setViewStructure] = useState(null);
  const [offices, setOffices] = useState([]);
  const [components, setComponents] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [filterOfficeId, setFilterOfficeId] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCycle, setFilterCycle] = useState("all");

  const officeNameById = useMemo(() => new Map(offices.map((o) => [String(o.id), o.name])), [offices]);
  const filteredStructures = useMemo(() => {
    let list = structures;
    if (filterOfficeId) {
      list = list.filter((s) => Array.isArray(s.officeIds) && s.officeIds.some((x) => String(x) === String(filterOfficeId)));
    }
    if (filterStatus !== "all") list = list.filter((s) => s.status === filterStatus);
    if (filterCycle !== "all") list = list.filter((s) => s.cycle === filterCycle);
    return list;
  }, [structures, filterOfficeId, filterStatus, filterCycle]);
  const total = filteredStructures.length;
  const slice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredStructures.slice(start, start + PAGE_SIZE);
  }, [page, filteredStructures]);
  const availableComponents = useMemo(() => {
    if (!builder.officeId) return [];
    return components.filter((c) => c.isActive && String(c.officeId) === String(builder.officeId));
  }, [components, builder.officeId]);
  const draftCount = useMemo(() => structures.filter((d) => d.status === "draft").length, [structures]);
  const publishedCount = useMemo(() => structures.filter((d) => d.status === "published").length, [structures]);
  const archivedCount = useMemo(() => structures.filter((d) => d.status === "archived").length, [structures]);
  const canAddStructureRows = Boolean(builder.officeId) && !loadingSources && availableComponents.length > 0;
  const showNoComponentsForWorkplace = Boolean(builder.officeId) && !loadingSources && availableComponents.length === 0;
  useEffect(() => {
    setPage(1);
  }, [filterOfficeId, filterStatus, filterCycle]);

  const loadSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      const [offRes, compRes] = await Promise.all([axiosBasic.get(SalaryComponent.Offices), axiosBasic.get(SalaryComponent.Get)]);
      setOffices(unwrapArray(offRes).map(normalizeOffice).filter((o) => o.id));
      setComponents(unwrapArray(compRes).map(normalizeSalaryComponent).filter((c) => c.id));
    } catch {
      Common.showErrorMessage(t("app.payroll.structures.loadSourcesError") || "Could not load workplaces/components.");
      setOffices([]);
      setComponents([]);
    } finally {
      setLoadingSources(false);
    }
  }, [t]);
  const loadStructures = useCallback(async () => {
    try {
      const res = await axiosBasic.get(PayrollStructure.Get);
      const list = unwrapArray(res).map((x) => normalizeStructure(x, officeNameById)).filter((x) => x.id);
      setStructures(list);
    } catch {
      Common.showErrorMessage(t("app.payroll.structures.loadListError") || "Could not load payroll structures.");
    }
  }, [officeNameById, t]);
  useEffect(() => {
    loadSources();
  }, [loadSources]);
  useEffect(() => {
    loadStructures();
  }, [loadStructures]);
  useEffect(() => {
    if (!builder.officeId) return;
    const allowed = new Set(availableComponents.map((c) => c.id));
    setBuilder((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.componentId && !allowed.has(String(r.componentId)) ? { ...r, componentId: "" } : r)) }));
  }, [builder.officeId, availableComponents]);

  const addRow = () => setBuilder((prev) => ({ ...prev, rows: [...prev.rows, emptyComponentRow()] }));
  const removeRow = (id) => setBuilder((prev) => ({ ...prev, rows: prev.rows.length <= 1 ? prev.rows : prev.rows.filter((x) => x.id !== id) }));
  const updateRow = (id, patch) => setBuilder((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const openCreate = () => {
    if (!withWorkplace()) return;
    setBuilderMode("create");
    setEditingId("");
    setAttemptedSave(false);
    setBuilder(emptyBuilder());
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setBuilderMode("edit");
    fetchDetailAndApply(row, "edit");
  };
  const openView = (row) => fetchDetailAndApply(row, "view");
  const closeModal = () => {
    if (saving) return;
    setAttemptedSave(false);
    setModalOpen(false);
  };
  const closeView = () => {
    if (viewLoading) return;
    setViewOpen(false);
    setViewStructure(null);
  };

  const applyDetailToBuilder = useCallback((detail) => {
    setEditingId(String(detail.id ?? ""));
    setBuilder({
      name: detail.name,
      description: detail.description || "",
      officeId: detail.officeId || "",
      cycle: detail.cycle,
      offCycle: detail.offCycle,
      rows:
        Array.isArray(detail.lines) && detail.lines.length > 0
          ? detail.lines.map((ln) => ({
              id: String(ln.id ?? ln.Id ?? `${Date.now()}-${Math.random()}`),
              componentId: String(ln.salaryComponentId ?? ln.SalaryComponentId ?? ""),
              unit: Number(ln.amountType ?? ln.AmountType ?? 0) === 1 ? "fixed" : "percent",
              value: String(ln.amountValue ?? ln.AmountValue ?? ""),
            }))
          : [emptyComponentRow()],
    });
  }, []);

  const fetchDetailAndApply = useCallback(
    async (row, mode) => {
      const id = String(row?.id ?? "").trim();
      if (!id) return;
      if (mode === "view") {
        setViewOpen(true);
        setViewLoading(true);
      } else {
        setModalOpen(true);
        setSaving(true);
      }
      try {
        const res = await axiosBasic.get(PayrollStructure.DetailBase + encodeURIComponent(id));
        const detailRaw = unwrapOne(res);
        if (!detailRaw) {
          Common.showErrorMessage(t("app.payroll.structures.loadDetailError") || "Could not load payroll structure detail.");
          if (mode === "view") closeView();
          else setModalOpen(false);
          return;
        }
        const detail = normalizeStructure(detailRaw, officeNameById);
        if (mode === "view") {
          setViewStructure({
            ...detail,
            attachedSalaryComponents: Array.isArray(detailRaw?.attachedSalaryComponents || detailRaw?.AttachedSalaryComponents)
              ? detailRaw.attachedSalaryComponents || detailRaw.AttachedSalaryComponents
              : [],
          });
          return;
        }
        applyDetailToBuilder(detail);
      } catch {
        Common.showErrorMessage(t("app.payroll.structures.loadDetailError") || "Could not load payroll structure detail.");
        if (mode === "view") closeView();
        else setModalOpen(false);
      } finally {
        if (mode === "view") setViewLoading(false);
        else setSaving(false);
      }
    },
    [PayrollStructure.DetailBase, applyDetailToBuilder, closeView, officeNameById, t]
  );

  const saveStructure = async (nextStatus) => {
    setAttemptedSave(true);
    const name = builder.name.trim();
    if (!name) return Common.showErrorMessage(t("app.payroll.structures.fieldNamePh"));
    const officeIds = builder.officeId ? [Number(builder.officeId)] : [];
    if (officeIds.length === 0 || Number.isNaN(officeIds[0])) return Common.showErrorMessage(t("app.payroll.common.selectOffice"));
    const invalidRow = builder.rows.find((r) => {
      if (!String(r?.componentId ?? "").trim()) return false;
      return Boolean(validateLineValue(r, t));
    });
    if (invalidRow) return Common.showErrorMessage(validateLineValue(invalidRow, t));
    const lines = builder.rows
      .map((r, idx) => ({
        Id: builderMode === "edit" ? String(r.id || "") : "",
        PayrollStructureId: builderMode === "edit" ? editingId : "",
        SalaryComponentId: String(r.componentId || ""),
        AmountType: UNIT_TO_ENUM[r.unit] ?? 0,
        AmountValue: Number.parseFloat(String(r.value || "").trim()),
        SortOrder: idx,
        IsActive: true,
      }))
      .filter((ln) => ln.SalaryComponentId && Number.isFinite(ln.AmountValue));
    if (lines.length === 0) return Common.showErrorMessage(t("app.payroll.structures.selectComponent"));
    const selected = getSelectedCompany();
    const fromCompany = selected && typeof selected === "object" ? selected.id ?? selected.Id ?? selected.companyId ?? selected.CompanyId : "";
    const user = getUserInfo();
    const companyId = String(fromCompany ?? user?.companyId ?? user?.CompanyId ?? "");
    if (!companyId) return Common.showErrorMessage(t("app.payroll.salaryComponents.companyMissing"));
    const payload = {
      Structure: {
        Id: builderMode === "edit" ? editingId : "",
        CompanyId: companyId,
        Name: name,
        Description: builder.description.trim().slice(0, 2000),
        Status: STATUS_TO_ENUM[nextStatus] ?? 0,
        PaymentCycle: CYCLE_TO_ENUM[builder.cycle] ?? 0,
        AllowOffCycleRuns: Boolean(builder.offCycle),
        IsActive: true,
      },
      OfficeIds: officeIds,
      Lines: lines,
    };
    setSaving(true);
    try {
      const res = await axiosBasic.post(PayrollStructure.Save, payload);
      const saved = unwrapOne(res);
      if (!saved) return Common.showErrorMessage(t("app.payroll.structures.saveError") || "Could not save payroll structure.");
      const normalized = normalizeStructure(saved, officeNameById);
      setStructures((prev) => {
        const idx = prev.findIndex((p) => String(p.id) === String(normalized.id));
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = normalized;
          return next;
        }
        return [normalized, ...prev];
      });
      Common.showSuccessMessage(nextStatus === "published" ? t("app.payroll.structures.publishSuccess") || "Policy published." : t("app.payroll.structures.draftSaved") || "Draft saved.");
      setAttemptedSave(false);
      setModalOpen(false);
    } catch {
      Common.showErrorMessage(t("app.payroll.structures.saveError") || "Could not save payroll structure.");
    } finally {
      setSaving(false);
    }
  };
  const saveDraft = () => saveStructure("draft");
  const publishPolicy = () => saveStructure("published");
  const publishFromGrid = async (row) => {
    const id = String(row?.id ?? "").trim();
    if (!id || row?.status === "published") return;
    setPublishingId(id);
    try {
      const res = await axiosBasic.post(PayrollStructure.PublishBase + encodeURIComponent(id));
      const saved = unwrapOne(res);
      if (!saved) {
        Common.showErrorMessage(t("app.payroll.structures.publishError") || "Could not publish payroll structure.");
        return;
      }
      const normalized = normalizeStructure(saved, officeNameById);
      setStructures((prev) => prev.map((p) => (String(p.id) === id ? { ...p, ...normalized } : p)));
      Common.showSuccessMessage(t("app.payroll.structures.publishSuccess") || "Policy published.");
    } catch {
      Common.showErrorMessage(t("app.payroll.structures.publishError") || "Could not publish payroll structure.");
    } finally {
      setPublishingId("");
    }
  };
  const archiveFromGrid = async (row) => {
    const id = String(row?.id ?? "").trim();
    if (!id || row?.status === "archived") return;
    setArchivingId(id);
    try {
      const res = await axiosBasic.patch(PayrollStructure.Archive, id, { headers: { "Content-Type": "application/json" } });
      const saved = unwrapOne(res);
      if (!saved) {
        Common.showErrorMessage(t("app.payroll.structures.archiveError") || "Could not archive payroll structure.");
        return;
      }
      const normalized = normalizeStructure(saved, officeNameById);
      setStructures((prev) => prev.map((p) => (String(p.id) === id ? { ...p, ...normalized } : p)));
      Common.showSuccessMessage(t("app.payroll.structures.archiveSuccess") || "Policy archived.");
    } catch {
      Common.showErrorMessage(t("app.payroll.structures.archiveError") || "Could not archive payroll structure.");
    } finally {
      setArchivingId("");
    }
  };

  const columns = useMemo(
    () => [
      { key: "name", label: t("app.payroll.structures.colName"), sortable: false },
      { key: "cycle", label: t("app.payroll.structures.colCycle"), sortable: false },
      { key: "offices", label: t("app.payroll.structures.colOffices"), sortable: false },
      { key: "offCycle", label: t("app.payroll.structures.colOffCycle"), sortable: false },
      { key: "status", label: t("app.payroll.structures.colStatus"), sortable: false },
      { key: "actions", label: t("app.payroll.common.actions"), sortable: false, headerClassName: payrollActionsHeaderClass },
    ],
    [t]
  );
  const clearFilters = () => {
    setFilterOfficeId("");
    setFilterStatus("all");
    setFilterCycle("all");
  };

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader icon={<i className="bi bi-diagram-3" />} title={t("app.payroll.structures.titleCased") || "Payroll Structures"} description={t("app.payroll.structures.subtitle")} />

      <section className="payroll-sc-toolbar-card mb-3" aria-label={t("app.payroll.structures.filterToolbarLabel") || "Filter payroll structures"}>
        <div className="payroll-sc-toolbar-card__head">
          <div>
            <h2 className="payroll-sc-toolbar-card__title">{t("app.payroll.structures.filterToolbarLabel") || "Filters & Actions"}</h2>
            <p className="payroll-sc-toolbar-card__subtitle text-muted mb-0">{t("app.payroll.structures.filterHint") || "Refine list by workplace, status, and cycle."}</p>
          </div>
          <div className="payroll-sc-toolbar-card__actions payroll-sc-toolbar-card__actions--equal-pair">
            <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
              <i className="bi bi-arrow-counterclockwise me-1" aria-hidden />
              {t("app.payroll.common.clearFilters") || "Clear filters"}
            </button>
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" aria-hidden />
              {t("app.payroll.structures.new")}
            </button>
          </div>
        </div>
        <div className="payroll-sc-toolbar-card__body">
          <div className="payroll-sc-toolbar-filters-row">
            <div className="payroll-sc-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-ps-filter-office">
                {t("app.payroll.structures.filterOfficeLabel") || t("app.payroll.common.office") || "Workplace"}
              </label>
              <select id="payroll-ps-filter-office" className="form-select form-select-sm" value={filterOfficeId} onChange={(e) => setFilterOfficeId(e.target.value)}>
                <option value="">{t("app.payroll.structures.filterOfficeAll") || "All workplaces"}</option>
                {offices.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.countryName ? `${o.name} — ${o.countryName}` : o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="payroll-sc-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-ps-filter-status">
                {t("app.payroll.structures.filterStatusLabel") || "Status"}
              </label>
              <select id="payroll-ps-filter-status" className="form-select form-select-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">{t("app.payroll.structures.filterStatusAll") || "All"}</option>
                <option value="published">{t("app.payroll.structures.statusPublished")}</option>
                <option value="draft">{t("app.payroll.structures.statusDraft")}</option>
                <option value="archived">{t("app.payroll.structures.statusArchived")}</option>
              </select>
            </div>
            <div className="payroll-sc-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-ps-filter-cycle">
                {t("app.payroll.structures.filterCycleLabel") || t("app.payroll.structures.colCycle")}
              </label>
              <select id="payroll-ps-filter-cycle" className="form-select form-select-sm" value={filterCycle} onChange={(e) => setFilterCycle(e.target.value)}>
                <option value="all">{t("app.payroll.structures.filterCycleAll") || "All cycles"}</option>
                <option value="monthly">{t("app.payroll.structures.cycleMonthly")}</option>
                <option value="biweekly">{t("app.payroll.structures.cycleBiweekly")}</option>
                <option value="weekly">{t("app.payroll.structures.cycleWeekly")}</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <div className="payroll-ps-status-strip" aria-label={t("app.payroll.structures.statusStripAria")}>
        <span className="payroll-ps-status-strip__title">{t("app.payroll.structures.statusStripTitle")}</span>
        <div className="payroll-ps-status-strip__chips">
          <span className="payroll-ps-status-chip payroll-ps-status-chip--neutral">
            <i className="bi bi-collection me-1" aria-hidden />
            {total} {t("app.payroll.structures.statPolicies")}
          </span>
          <span className="payroll-ps-status-chip payroll-ps-status-chip--published">
            <i className="bi bi-check-circle me-1" aria-hidden />
            {publishedCount} {t("app.payroll.structures.statPublished")}
          </span>
          <span className="payroll-ps-status-chip payroll-ps-status-chip--draft">
            <i className="bi bi-pencil-square me-1" aria-hidden />
            {draftCount} {t("app.payroll.structures.statDrafts")}
          </span>
          <span className="payroll-ps-status-chip payroll-ps-status-chip--archived">
            <i className="bi bi-archive me-1" aria-hidden />
            {archivedCount} {t("app.payroll.structures.statArchived")}
          </span>
        </div>
      </div>

      <section className="payroll-ps-shell">
        <div className="payroll-ps-panel payroll-ps-panel--list">
          <section className="ts-appr-stats payroll-ps-stats payroll-ps-stats--four mb-3">
            <div className="ts-appr-stat-card ts-appr-stat-card--total">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon"><i className="bi bi-layers" /></span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">{total}</span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.structures.statPolicies")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-appr-stat-card ts-appr-stat-card--approved">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon"><i className="bi bi-check2-circle" /></span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">{publishedCount}</span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.structures.statPublished")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-appr-stat-card ts-appr-stat-card--awaiting">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon"><i className="bi bi-pencil-square" /></span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">{draftCount}</span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.structures.statDrafts")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="ts-appr-stat-card ts-appr-stat-card--returned">
              <div className="ts-appr-stat-card__accent" aria-hidden />
              <div className="ts-appr-stat-card__inner">
                <div className="ts-appr-stat-card__top">
                  <span className="ts-appr-stat-card__icon"><i className="bi bi-archive" /></span>
                  <div className="ts-appr-stat-card__figures">
                    <span className="ts-appr-stat-card__value">{archivedCount}</span>
                    <span className="ts-appr-stat-card__label">{t("app.payroll.structures.statArchived")}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <PayrollDataGrid
            columns={columns}
            rows={slice}
            getRowKey={(row) => String(row.id)}
            emptyMessage={t("app.payroll.common.noResults")}
            renderRow={(row) => (
              <>
                <td className="fw-semibold">{row.name}</td>
                <td>{row.cycle}</td>
                <td>{row.offices}</td>
                <td>{row.offCycle ? t("app.payroll.common.yes") : t("app.payroll.common.no")}</td>
                <td>
                  <span className={`payroll-badge ${row.status === "published" ? "payroll-badge--active" : row.status === "archived" ? "payroll-badge--pending" : "payroll-badge--draft"}`}>
                    {row.status === "published" ? t("app.payroll.structures.statusPublished") : row.status === "archived" ? t("app.payroll.structures.statusArchived") : t("app.payroll.structures.statusDraft")}
                  </span>
                </td>
                <td className={payrollActionsCellClass} onClick={(e) => e.stopPropagation()}>
                  <div className="documents-actions-inline payroll-ps-actions-inline">
                    <button type="button" className="documents-grid-action-btn documents-grid-action-btn--view documents-action-compact" onClick={() => openView(row)}>
                      {t("app.payroll.common.view")}
                    </button>
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--edit documents-action-compact"
                      onClick={() => publishFromGrid(row)}
                      disabled={row.status === "published" || publishingId === String(row.id)}
                      title={row.status === "archived" ? t("app.payroll.structures.republishArchivedHint") : undefined}
                    >
                      {publishingId === String(row.id) ? t("app.common.loading") : t("app.payroll.structures.publishAction")}
                    </button>
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--delete documents-action-compact"
                      onClick={() => archiveFromGrid(row)}
                      disabled={row.status === "archived" || archivingId === String(row.id)}
                    >
                      {archivingId === String(row.id) ? t("app.common.loading") : t("app.payroll.structures.archiveAction")}
                    </button>
                    <button type="button" className="documents-grid-action-btn documents-grid-action-btn--edit documents-action-compact" onClick={() => openEdit(row)}>
                      {t("app.payroll.common.edit")}
                    </button>
                  </div>
                </td>
              </>
            )}
          />

          <div className="documents-pagination documents-pagination--grid-pager d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
            <span className="documents-pagination-total text-muted small">
              {total === 0 ? t("app.payroll.common.noResults") : t("app.payroll.common.showingRange", { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
            </span>
            <GridPager className="documents-rc-pagination" onChange={(p) => setPage(p)} current={page} total={total} pageSize={PAGE_SIZE} showLessItems showTitle={false} showSizeChanger={false} />
          </div>
        </div>
      </section>

      <PayrollModal
        open={modalOpen}
        onClose={closeModal}
        size="xlarge"
        variant="salary"
        headerIcon={<i className="bi bi-diagram-3" />}
        titleId="payroll-structure-modal-title"
        title={builderMode === "create" ? t("app.payroll.structures.new") : t("app.payroll.common.edit")}
        subtitle={builderMode === "create" ? t("app.payroll.structures.modalTitle") : builder.name || t("app.payroll.structures.modalTitle")}
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeModal} disabled={saving}>{t("app.payroll.common.cancel")}</button>
            <button type="button" className="att-btn payroll-ps-btn--draft" onClick={saveDraft} disabled={saving}>
              <i className="bi bi-journal-check" aria-hidden />
              <span>{t("app.payroll.structures.saveDraft") || "Save Draft"}</span>
            </button>
            <button type="button" className="att-btn att-btn--save-primary payroll-ps-btn--publish" onClick={publishPolicy} disabled={saving}>
              <i className="bi bi-send-check" aria-hidden />
              <span>{builderMode === "edit" ? t("app.payroll.structures.updatePolicy") : t("app.payroll.structures.publishPolicy")}</span>
            </button>
          </>
        }
      >
        <div className="payroll-sc-modal-form payroll-sc-modal-form--salary">
          <div className="row g-2">
            <div className="col-md-7 col-12">
              <label className="att-modal-field-label" htmlFor="payroll-ps-name">{t("app.payroll.structures.fieldName")}</label>
              <input id="payroll-ps-name" className="form-control" placeholder={t("app.payroll.structures.fieldNamePh")} value={builder.name} onChange={(e) => setBuilder((prev) => ({ ...prev, name: e.target.value }))} disabled={saving} />
            </div>
            <div className="col-md-5 col-12">
              <label className="att-modal-field-label" htmlFor="payroll-ps-cycle">{t("app.payroll.structures.fieldCycle")}</label>
              <select id="payroll-ps-cycle" className="form-select" value={builder.cycle} onChange={(e) => setBuilder((prev) => ({ ...prev, cycle: e.target.value }))} disabled={saving}>
                <option value="monthly">{t("app.payroll.structures.cycleMonthly")}</option>
                <option value="biweekly">{t("app.payroll.structures.cycleBiweekly")}</option>
                <option value="weekly">{t("app.payroll.structures.cycleWeekly")}</option>
              </select>
            </div>
            <div className="col-12">
              <label className="att-modal-field-label" htmlFor="payroll-ps-workplace">{t("app.payroll.structures.fieldWorkplaces")}</label>
              <select id="payroll-ps-workplace" className="form-select" value={builder.officeId} onChange={(e) => setBuilder((prev) => ({ ...prev, officeId: e.target.value }))} disabled={loadingSources || saving}>
                <option value="">{t("app.payroll.common.selectOffice")}</option>
                {offices.map((o) => <option key={o.id} value={o.id}>{o.countryName ? `${o.name} — ${o.countryName}` : o.name}</option>)}
              </select>
              <p className="text-muted mt-1 mb-0">{t("app.payroll.structures.workplaceComponentHint") || "Components list is filtered by selected workplace."}</p>
            </div>
            <div className="col-12">
              <label className="att-modal-field-label" htmlFor="payroll-ps-desc">{t("app.payroll.structures.fieldDescription")}</label>
              <textarea id="payroll-ps-desc" rows={3} className="form-control" placeholder={t("app.payroll.structures.fieldDescriptionPh")} value={builder.description} onChange={(e) => setBuilder((prev) => ({ ...prev, description: e.target.value }))} disabled={saving} />
            </div>
          </div>

          <label className="payroll-sc-statutory">
            <input type="checkbox" className="payroll-sc-statutory__input" checked={builder.offCycle} onChange={(e) => setBuilder((prev) => ({ ...prev, offCycle: e.target.checked }))} disabled={saving} />
            <span className="payroll-sc-statutory__copy">
              <span className="payroll-sc-statutory__title">{t("app.payroll.structures.fieldOffCycle")}</span>
              <span className="payroll-sc-statutory__hint">{t("app.payroll.structures.offCycleHint") || "Allow payroll runs outside standard cycle."}</span>
            </span>
          </label>

          <div className="payroll-ps-components-head">
            <h4>{t("app.payroll.structures.componentsLabel")}</h4>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={addRow}
              disabled={saving || !canAddStructureRows}
              title={
                saving
                  ? undefined
                  : !builder.officeId
                    ? t("app.payroll.common.selectOffice")
                    : loadingSources
                      ? t("app.payroll.structures.addRowLoadingHint")
                      : availableComponents.length === 0
                        ? t("app.payroll.structures.addRowDisabledHint")
                        : undefined
              }
            >
              <i className="bi bi-plus-lg me-1" aria-hidden />
              {t("app.payroll.structures.addComponent")}
            </button>
          </div>

          {showNoComponentsForWorkplace ? (
            <div className="payroll-ps-modal-empty" role="status">
              <div className="payroll-ps-modal-empty__icon" aria-hidden>
                <i className="bi bi-inboxes" />
              </div>
              <div className="payroll-ps-modal-empty__text">
                <p className="payroll-ps-modal-empty__title">{t("app.payroll.structures.noComponentsCardTitle")}</p>
                <p className="payroll-ps-modal-empty__lead">{t("app.payroll.structures.noComponentsCardLead")}</p>
              </div>
              <div className="payroll-ps-modal-empty__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary payroll-ps-modal-empty__btn"
                  onClick={() => history.push("/payroll/salary-components")}
                  disabled={loadingSources || saving}
                  title={loadingSources ? t("app.payroll.structures.openSalaryComponentsLoading") : saving ? t("app.payroll.structures.openSalaryComponentsSaving") : undefined}
                >
                  {t("app.payroll.structures.openSalaryComponents")}
                </button>
              </div>
            </div>
          ) : null}

          <div className="payroll-structure-rows">
            {builder.rows.map((r) => (
              <div key={r.id} className="payroll-structure-row">
                <div>
                  <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.colComponent")}</span>
                  <select className="form-select form-select-sm mt-1" value={r.componentId} onChange={(e) => updateRow(r.id, { componentId: e.target.value })} disabled={!builder.officeId || loadingSources || availableComponents.length === 0 || saving}>
                    <option value="">{t("app.payroll.structures.selectComponent")}</option>
                    {availableComponents.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.colUnit")}</span>
                  <select
                    className="form-select form-select-sm mt-1"
                    value={r.unit}
                    onChange={(e) => updateRow(r.id, { unit: e.target.value, value: "" })}
                    disabled={saving || !String(r.componentId || "").trim()}
                  >
                    <option value="percent">%</option>
                    <option value="fixed">{t("app.payroll.structures.unitFixed")}</option>
                  </select>
                </div>
                <div>
                  <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.colValue")}</span>
                  {(() => {
                    const hasComponent = String(r.componentId || "").trim();
                    const valueError = hasComponent && (attemptedSave || String(r.value || "").trim()) ? validateLineValue(r, t) : "";
                    return (
                      <>
                        <input
                          className={`form-control form-control-sm mt-1 ${valueError ? "is-invalid" : ""}`}
                          placeholder={t("app.payroll.structures.valuePh")}
                          inputMode={r.unit === "percent" ? "numeric" : "decimal"}
                          value={r.value}
                          onChange={(e) => {
                            const sanitized = sanitizeValueInput(e.target.value, r.unit);
                            if (sanitized === null) return;
                            updateRow(r.id, { value: sanitized });
                          }}
                          disabled={saving || !hasComponent}
                        />
                        {valueError ? <div className="invalid-feedback d-block">{valueError}</div> : null}
                      </>
                    );
                  })()}
                </div>
                <div className="pb-1 d-flex align-items-end">
                  <button type="button" className="btn btn-light btn-sm" onClick={() => removeRow(r.id)} aria-label={t("app.payroll.common.remove")} disabled={saving}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </PayrollModal>

      <PayrollModal
        open={viewOpen}
        onClose={closeView}
        size="large"
        variant="salary"
        headerIcon={<i className="bi bi-eye" />}
        titleId="payroll-structure-view-title"
        title={t("app.payroll.common.view")}
        subtitle={viewStructure?.name || t("app.payroll.structures.modalTitle")}
        footer={
          <button type="button" className="att-btn att-btn--ghost" onClick={closeView} disabled={viewLoading}>
            {t("app.common.close")}
          </button>
        }
      >
        {viewLoading || !viewStructure ? (
          <div className="py-2 text-muted">{t("app.common.loading")}</div>
        ) : (
          <div className="payroll-sc-modal-form payroll-sc-modal-form--salary">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.fieldCycle")}</span>
                <div>{viewStructure.cycle}</div>
              </div>
              <div className="col-12 col-md-6">
                <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.colStatus")}</span>
                <div>
                  <span className={`payroll-badge ${viewStructure.status === "published" ? "payroll-badge--active" : viewStructure.status === "archived" ? "payroll-badge--pending" : "payroll-badge--draft"}`}>
                    {viewStructure.status === "published" ? t("app.payroll.structures.statusPublished") : viewStructure.status === "archived" ? t("app.payroll.structures.statusArchived") : t("app.payroll.structures.statusDraft")}
                  </span>
                </div>
              </div>
              <div className="col-12">
                <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.fieldWorkplaces")}</span>
                <div>{viewStructure.offices || "—"}</div>
              </div>
              <div className="col-12">
                <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.fieldDescription")}</span>
                <div>{viewStructure.description || "—"}</div>
              </div>
            </div>
            <hr className="payroll-sc-modal-form__divider" />
            <div>
              <span className="payroll-modal-field-label-sm">{t("app.payroll.structures.componentsLabel")}</span>
              <ul className="mb-0 mt-2">
                {(Array.isArray(viewStructure.attachedSalaryComponents) ? viewStructure.attachedSalaryComponents : []).map((c) => (
                  <li key={String(c.id ?? c.Id ?? Math.random())}>{String(c.name ?? c.Name ?? "—")} {c.code || c.Code ? `(${String(c.code ?? c.Code)})` : ""}</li>
                ))}
                {(!Array.isArray(viewStructure.attachedSalaryComponents) || viewStructure.attachedSalaryComponents.length === 0) && <li>—</li>}
              </ul>
            </div>
          </div>
        )}
      </PayrollModal>

      {workplaceRequiredModal}
    </div>
  );
};

export default PayrollStructuresPage;
